import styled, { css } from 'styled-components';
import { Pagination as BasePagination } from '@dsch/pagination';
import {
  ChevronBackOutlineIc,
  ChevronForwardOutlineIc,
  ChevronBackOutlineVariantIc,
  ChevronForwardOutlineVariantIc,
} from '@dsch/dd-icons';
import type { Paging } from '@dist-marketplace-frontend/types';

import GhostButton from '../Button/GhostButton/GhostButton';
import { donedealBreakpoints } from 'styled-system';

export interface PaginationProps extends Paging {
  goToStart?: () => void;
  goToEnd?: () => void;
  className?: string;
}

interface INavigationLinkButton {
  margin?: 'left' | 'right';
}

const MarginLeftCSS = css`
  margin-left: ${({ theme }) => theme.spacing.S8};

  ${donedealBreakpoints.media.large} {
    margin-left: 0;
  }
`;

const MarginRightCSS = css`
  margin-right: ${({ theme }) => theme.spacing.S8};

  ${donedealBreakpoints.media.large} {
    margin-right: ${({ theme }) => theme.spacing.M24};
  }
`;

const SPagination = styled(BasePagination)`
  ${({ theme }) => theme.fontSize.M14}
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.colors.GREY};
`;

const BookendGhostButtonContainer = styled.span`
  display: flex;
  align-items: center;

  ${donedealBreakpoints.media.large} {
    display: none;
  }
`;

const NavigationGhostButtonContainer = styled.span`
  display: inline-flex;
  align-items: center;
`;

/**
 * This component is pretty precious, but it was created to more closely align with DoneDeal's
 * GhostButton component.
 */
const NavigationGhostButton = styled(GhostButton)<INavigationLinkButton>`
  font-weight: 700;
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  height: 100%;
  ${({ theme }) => theme.donedeal.fontSize.M16};
  border-radius: ${({ theme }) => theme.donedeal.borderRadius.default};

  ${({ margin }) =>
    margin === 'right'
      ? MarginRightCSS
      : margin === 'left'
      ? MarginLeftCSS
      : undefined}

  // These are overrides specific for these styles in GhostButton which we don't want in this
  // Pagination component.
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border-bottom-color: transparent;
    background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
  }
`;

const PaginationLinks = styled.div<{ displayFirstAndLast: boolean }>`
  display: flex;
  // We only want to use space-between when we have first and last buttons, as that is the only
  // reason justify-content: space-between is needed. In this case, we want the central 1, 2, ... N
  // buttons to be centered, and the first and last buttons to be on the edges.
  justify-content: ${({ displayFirstAndLast }) =>
    displayFirstAndLast ? 'space-between' : 'center'};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${donedealBreakpoints.media.large} {
    justify-content: center;
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const PageLinkButton = styled.button<{
  highlight?: boolean;
}>`
  display: ${({ highlight }) => (highlight ? 'block' : 'none')};
  height: 48px;
  color: ${({ highlight, theme }) =>
    highlight ? theme.colors.GREY_DARKER : theme.colors.GREY_DARK};
  font-weight: ${({ highlight, theme }) =>
    highlight ? theme.fontWeight.bold : theme.fontWeight.regular};
  background-color: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  ${({ highlight, theme }) =>
    highlight
      ? `border: 2px solid ${theme.colors.GREY_DARKER}`
      : `border: 1px solid ${theme.colors.GREY_DARK}`};
  padding: 0 ${({ theme }) => theme.spacing.M16};
  ${donedealBreakpoints.media.large} {
    margin-right: ${({ theme }) => theme.spacing.M24};
    display: block;
  }
  &:hover {
    color: ${({ highlight, theme }) =>
      highlight ? theme.colors.GREY_DARKER : theme.colors.GREY_DARK};
  }
`;

const StyledBreak = styled.span`
  display: none;

  ${donedealBreakpoints.media.large} {
    ${({ theme }) => theme.fontSize.M16}
    display: flex;
    justify-content: center;
    align-items: flex-end;
    height: 40px;
    cursor: default;
    margin-right: ${({ theme }) => theme.spacing.M24};
  }
`;

const PaginationResultsData = styled.p`
  ${({ theme }) => theme.fontSize.M14};
  text-align: center;
`;

/**
 * @deprecated replace with the one from libs when possible.
 */
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IE').format(num);
};

function Pagination(props: PaginationProps): JSX.Element {
  const {
    currentPage,
    pageSize,
    totalPages,
    totalResults,
    goToStart,
    goToEnd,
  } = props;
  const formattedDisplayingFrom = formatNumber(
    (currentPage - 1) * pageSize + 1,
  );
  const formattedDisplayingTo = formatNumber(
    pageSize * currentPage > totalResults
      ? totalResults
      : pageSize * currentPage,
  );
  const formattedTotalResults = formatNumber(totalResults);
  const displayFirstAndLast = Boolean(goToStart && goToEnd);

  return (
    <>
      <PaginationLinks displayFirstAndLast={displayFirstAndLast}>
        {displayFirstAndLast && (
          <BookendGhostButtonContainer>
            <NavigationGhostButton
              onClick={props.goToStart}
              disabled={currentPage === 1}
              height="m"
              aria-label="Go to first page"
            >
              <ChevronBackOutlineVariantIc />
            </NavigationGhostButton>
          </BookendGhostButtonContainer>
        )}
        <SPagination
          searchQueryGroup=""
          renderBreakView={(props) => (
            <StyledBreak {...props}>{'...'}</StyledBreak>
          )}
          renderPageButton={(props) => {
            const { onClick, text } = props;
            return (
              <PageLinkButton
                onClick={onClick}
                highlight={currentPage === text}
                aria-current={currentPage === text ? 'page' : undefined}
              >
                {formatNumber(text)}
              </PageLinkButton>
            );
          }}
          renderPrevButton={(props) => (
            <NavigationGhostButtonContainer>
              <NavigationGhostButton
                data-testid="go-to-prev-page"
                margin="right"
                {...props}
                height="m"
              >
                <ChevronBackOutlineIc />
                <span>Prev</span>
              </NavigationGhostButton>
            </NavigationGhostButtonContainer>
          )}
          renderNextButton={(props) => (
            <NavigationGhostButtonContainer>
              <NavigationGhostButton
                data-testid="go-to-next-page"
                margin="left"
                height="m"
                {...props}
              >
                <span>Next</span>
                <ChevronForwardOutlineIc />
              </NavigationGhostButton>
            </NavigationGhostButtonContainer>
          )}
          {...props}
        />
        {displayFirstAndLast && (
          <BookendGhostButtonContainer>
            <NavigationGhostButton
              onClick={goToEnd}
              disabled={currentPage === totalPages}
              height="m"
              aria-label="Go to last page"
            >
              <ChevronForwardOutlineVariantIc />
            </NavigationGhostButton>
          </BookendGhostButtonContainer>
        )}
      </PaginationLinks>
      <PaginationResultsData data-testid="pagination-results">
        Showing {formattedDisplayingFrom} - {formattedDisplayingTo} of{' '}
        {formattedTotalResults}
      </PaginationResultsData>
    </>
  );
}

export { Pagination };
